import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  TextField,
  Modal,
  Stack,
  Avatar,
  MenuItem,
  Snackbar,
  Container,
  alpha,
  useTheme,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Add, Close, Laptop, Home, CalendarToday, AccessTime, Note, Cancel } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createSession,
  clearSession,
  getTutorSessions,
  cancelSession,
  approveSession,
  rejectSession,
} from "../../state/slices/session";
import { CheckCircle, Cancel as CancelIcon, Pending } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import Loading from "../../components/loading/loading";
import { Alert } from "@mui/material";
import { getStudents } from "../../state/slices/tutor";

const TUTORING_TYPE = {
  IN_PERSON: "IN_PERSON",
  ONLINE: "REMOTE",
};

// Move CreateMeetingModalPage outside to prevent remounting on parent re-renders
const CreateMeetingModalPage = ({ open, handleClose, student, dispatch, isTutor }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [meeting, setMeeting] = React.useState({
    name: student?.name || '',
    date: dayjs(),
    startTime: dayjs(),
    endTime: dayjs().add(1, "hour"),
    tutoring_type: "",
    note: "",
  });
  const [errors, setErrors] = React.useState({});
  const [inviteOtherStudents, setInviteOtherStudentsState] = React.useState(false);
  const inviteOtherStudentsRef = React.useRef(inviteOtherStudents);
  // Wrap setInviteOtherStudents to track state changes
  const setInviteOtherStudents = React.useCallback((value) => {
    const newValue = typeof value === 'function' ? value(inviteOtherStudentsRef.current) : value;
    inviteOtherStudentsRef.current = newValue;
    setInviteOtherStudentsState(newValue);
  }, []);
  // Update ref when state changes
  React.useEffect(() => {
    inviteOtherStudentsRef.current = inviteOtherStudents;
  }, [inviteOtherStudents]);
  const [selectedAdditionalStudents, setSelectedAdditionalStudents] = React.useState([]);
  const [tutorStudents, setTutorStudents] = React.useState([]);
  const [loadingStudents, setLoadingStudents] = React.useState(false);

  // Load tutor's students when modal opens and inviteOtherStudents is checked
  const currentStudentId = student?.student?.id;
  const prevInviteOtherStudentsRef = React.useRef(inviteOtherStudents);
  React.useEffect(() => {
    // Only fetch when inviteOtherStudents changes from false to true (checkbox is checked)
    const justChecked = !prevInviteOtherStudentsRef.current && inviteOtherStudents;
    if (open && justChecked && isTutor && currentStudentId && tutorStudents.length === 0 && !loadingStudents) {
      setLoadingStudents(true);
      dispatch(getStudents())
        .then((result) => {
          if (result.payload) {
            // Filter out the current student and map to options
            const students = result.payload
              .filter(s => s.student.id !== currentStudentId)
              .map(s => ({
                id: s.student.id,
                name: `${s.student.firstName || ''} ${s.student.lastName || ''}`.trim() || s.student.email || 'Unknown',
                email: s.student.email
              }));
            setTutorStudents(students);
          }
        })
        .catch((err) => {
          // Error handled silently
        })
        .finally(() => {
          setLoadingStudents(false);
        });
    }
    // Update ref after checking
    prevInviteOtherStudentsRef.current = inviteOtherStudents;
  }, [open, inviteOtherStudents, isTutor, dispatch, currentStudentId]);

  const combineDateAndTime = (date, time) => {
    return dayjs(date)
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);
  };

  const validate = () => {
    let temp = {};
    temp.tutoringType = meeting.tutoring_type
      ? ""
      : t('classRoom.tutoringTypeRequired');
    temp.note = meeting.note ? "" : t('classRoom.noteRequired');
    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = () => {
    console.log('handleSubmit called', { meeting, errors });
    
    // Clear previous errors
    setErrors({});
    
    // Validate required fields
    const isValid = validate();
    console.log('Validation result:', isValid);
    if (!isValid) {
      console.log('Validation failed, errors:', errors);
      return;
    }
    
    try {
      // Ensure dates are converted to ISO strings for proper API communication
      const startDateTime = combineDateAndTime(meeting.date, meeting.startTime);
      const endDateTime = combineDateAndTime(meeting.date, meeting.endTime);
      
      // Validate that end time is after start time using timestamp comparison
      if (!endDateTime || !startDateTime) {
        setErrors({ timeRange: 'Invalid date/time values' });
        return;
      }
      
      // Compare timestamps - endTime must be greater than startTime
      if (endDateTime.valueOf() <= startDateTime.valueOf()) {
        setErrors({ timeRange: t('classRoom.endTimeMustBeAfterStartTime') || 'End time must be after start time' });
        return;
      }
      
      // Build studentIds array
      let studentIds = [student.student.id];
      if (inviteOtherStudents && selectedAdditionalStudents.length > 0) {
        studentIds = [student.student.id, ...selectedAdditionalStudents.map(s => s.id)];
      }

      const newMeeting = {
        studentIds: studentIds,
        tutoringType: meeting.tutoring_type,
        note: meeting.note,
        startingTime: startDateTime.toISOString(),
        endingTime: endDateTime.toISOString(),
      };
      
      console.log('Submitting session:', newMeeting);
      dispatch(createSession(newMeeting));
      handleClose();
      // Reset form
      setInviteOtherStudents(false);
      setSelectedAdditionalStudents([]);
    } catch (error) {
      console.error('Error submitting session:', error);
      setErrors({ submit: 'Failed to create session. Please try again.' });
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      BackdropProps={{
        sx: {
          bgcolor: alpha('#000', 0.5),
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', md: 900 },
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.3)}`,
          maxHeight: '90vh',
          overflowY: 'auto',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            {t('classRoom.createSession')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'text.secondary' }}>
            <Close />
          </IconButton>
        </Box>
        
        <Box sx={{ p: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={3}>
               <Grid item xs={12} md={6}>
                 <Box
                   sx={{
                     border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                     borderRadius: 3,
                     p: 2,
                     bgcolor: alpha(theme.palette.primary.main, 0.02),
                     overflow: 'auto',
                     maxHeight: '70vh',
                     '& .MuiPickersCalendarHeader-root': {
                       flexDirection: 'row',
                       alignItems: 'center',
                     },
                     '& .MuiDayCalendar-root': {
                       width: '100%',
                     },
                   }}
                 >
                   <DemoContainer components={["StaticDatePicker"]}>
                     <DemoItem label={t('classRoom.chooseDate')}>
                       <StaticDatePicker
                         orientation="portrait"
                         defaultValue={meeting.date}
                         onChange={(newValue) => {
                           setMeeting({ ...meeting, date: newValue });
                         }}
                         sx={{
                           '& .MuiPickersCalendarHeader-root': {
                             paddingLeft: 2,
                             paddingRight: 2,
                           },
                           '& .MuiDayCalendar-header': {
                             justifyContent: 'space-around',
                           },
                           '& .MuiDayCalendar-weekContainer': {
                             justifyContent: 'space-around',
                           },
                         }}
                       />
                     </DemoItem>
                   </DemoContainer>
                 </Box>
               </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  <Box
                    sx={{
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      borderRadius: 3,
                      p: 2,
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    }}
                  >
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TimePicker
                          label={t('classRoom.startTime')}
                          value={meeting.startTime}
                          onChange={(newValue) => {
                            setMeeting({ ...meeting, startTime: newValue });
                          }}
                          sx={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TimePicker
                          label={t('classRoom.endTime')}
                          value={meeting.endTime}
                          onChange={(newValue) => {
                            setMeeting({ ...meeting, endTime: newValue });
                          }}
                          sx={{ width: '100%' }}
                        />
                      </Grid>
                    </Grid>
                    {errors.timeRange && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.timeRange}
                      </Typography>
                    )}
                    {errors.submit && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.submit}
                      </Typography>
                    )}
                  </Box>

                  <TextField
                    fullWidth
                    label={t('classRoom.tutoringType')}
                    value={meeting.tutoring_type}
                    error={Boolean(errors["tutoringType"])}
                    helperText={errors["tutoringType"]}
                    onChange={(event) => {
                      setMeeting({
                        ...meeting,
                        tutoring_type: event.target.value,
                      });
                    }}
                    select
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      }
                    }}
                  >
                    <MenuItem value={TUTORING_TYPE.IN_PERSON}>
                      {t('common.inPerson')}
                    </MenuItem>
                    <MenuItem value={TUTORING_TYPE.ONLINE}>{t('common.online')}</MenuItem>
                  </TextField>

                  <TextField
                    multiline
                    minRows={4}
                    fullWidth
                    label={t('classRoom.note')}
                    value={meeting.note}
                    onChange={(event) => {
                      setMeeting({
                        ...meeting,
                        note: event.target.value,
                      });
                    }}
                    error={Boolean(errors["note"])}
                    helperText={errors["note"]}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                      }
                    }}
                  />

                  {/* Invite Other Students - Only for Tutors */}
                  {isTutor && (
                    <>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={inviteOtherStudents}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setInviteOtherStudents(checked);
                              if (!checked) {
                                // Clear students when checkbox is unchecked
                                setTutorStudents([]);
                                setSelectedAdditionalStudents([]);
                              }
                            }}
                            color="primary"
                          />
                        }
                        label={t('classRoom.inviteOtherStudents') || 'Invite other students'}
                      />
                      {inviteOtherStudents && (
                        <Box sx={{ mt: 1 }}>
                          <Autocomplete
                            multiple
                            options={tutorStudents || []}
                            getOptionLabel={(option) => option?.name || ''}
                            value={selectedAdditionalStudents}
                            onChange={(event, newValue) => {
                              setSelectedAdditionalStudents(newValue || []);
                            }}
                            loading={loadingStudents}
                            disabled={loadingStudents}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('classRoom.selectStudents') || 'Select Students'}
                                placeholder={t('classRoom.selectStudentsPlaceholder') || 'Select additional students...'}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                                  }
                                }}
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  label={option?.name || ''}
                                  {...getTagProps({ index })}
                                  key={option?.id || index}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              ))
                            }
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{ 
                  borderRadius: 2,
                  py: 1.25,
                  px: 3,
                  fontWeight: 600,
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Button clicked, calling handleSubmit');
                  handleSubmit();
                }}
                type="button"
                sx={{
                  borderRadius: 2,
                  py: 1.25,
                  px: 3,
                  fontWeight: 600,
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }
                }}
              >
                {t('classRoom.createSession')}
              </Button>
            </Box>
          </LocalizationProvider>
        </Box>
      </Box>
    </Modal>
  );
};

const Detail_class_room = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const { sessions, loading, error, success, session } = useSelector(
    (state) => state.session
  );
  const userState = useSelector((state) => state.user);
  const user = userState?.switchedAccount ? userState.switchedAccount.studentData?.userId : userState?.user;
  const isTutor = user?.roles?.includes("TUTOR");
  const isStudent = user?.roles?.includes("STUDENT");
  const tutorState = useSelector((state) => state.tutor);
  const dispatch = useDispatch();
  
  // Get student from location state (may be null)
  const student = location.state?.student;

  // Handle case where location.state is null (direct navigation or page refresh)
  React.useEffect(() => {
    if (!location.state || !location.state.student) {
      // Redirect back to class room list if no student data
      navigate('/class-room', { replace: true });
    }
  }, [location.state, navigate]);

  const handleSessionsFetch = React.useCallback(() => {
    if (student && student.student && student.student.id) {
      dispatch(getTutorSessions(student.student.id));
    }
  }, [dispatch, student]);

  React.useEffect(() => {
    if (student) {
      handleSessionsFetch();
    }
  }, [handleSessionsFetch, student]);

  // Early return if no student data (but after all hooks)
  if (!location.state || !location.state.student) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Loading />
      </Box>
    );
  }

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const CreateMeetingButton = () => {
    // Only show button for tutors (students will request from student-detail-class-room)
    if (!isTutor) {
      return null;
    }
    return (
      <Button
        variant="contained"
        onClick={handleOpen}
        startIcon={<Add />}
        sx={{
          borderRadius: 3,
          py: 1.5,
          px: 3,
          fontWeight: 700,
          fontSize: '1rem',
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
          }
        }}
      >
        {t('classRoom.createSession')}
      </Button>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Snackbar
        open={Boolean((success && success !== "Sessions fetched successfully" && (success.includes("Session") || success.includes("approved") || success.includes("rejected"))) || error)}
        autoHideDuration={3000}
        onClose={() => {
          handleSessionsFetch();
          dispatch(clearSession());
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          severity={success ? "success" : "error"}
          sx={{ borderRadius: 2 }}
        >
          {success || error}
        </Alert>
      </Snackbar>

      <Container maxWidth="lg" sx={{ pt: 4 }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'white' }}>
            <Avatar
              alt={student.student.firstName + " " + student.student.lastName}
              src={student.student.profileImage}
              sx={{
                width: 120,
                height: 120,
                mx: 'auto',
                mb: 2,
                border: `4px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                mb: 3,
              }}
            >
              {student.student.firstName} {student.student.lastName} {t('classRoom.classRoom')}
            </Typography>
            <CreateMeetingButton />
          </Box>
        </Card>

        <CreateMeetingModalPage open={open} handleClose={handleClose} student={student} dispatch={dispatch} isTutor={isTutor} />
        
        {loading || session ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Loading />
          </Box>
        ) : sessions.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              textAlign: 'center',
            }}
          >
            <Typography variant="h5" sx={{ color: 'text.secondary', fontWeight: 600, mb: 1 }}>
              {t('classRoom.noSession')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('classRoom.createFirstSession')}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {sessions.map((meeting) => (
              <Grid item xs={12} sm={6} md={4} key={meeting._id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.2)}`,
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.date')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {dayjs(meeting.startingTime).format("DD/MM/YYYY")}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AccessTime sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.time')}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {dayjs(meeting.startingTime).format("hh:mm a")} - {dayjs(meeting.endingTime).format("hh:mm a")}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {meeting.tutoringType === "REMOTE" ? (
                          <Laptop sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        ) : (
                          <Home sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        )}
                        <Box>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            {t('classRoom.tutoringType')}
                          </Typography>
                          <Chip
                            label={meeting.tutoringType === "REMOTE" ? t('common.online') : t('common.inPerson')}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      </Box>

                      {meeting.note && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                          <Note sx={{ color: theme.palette.primary.main, fontSize: 20, mt: 0.5 }} />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                              {t('classRoom.note')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {meeting.note}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {meeting.status === 'PENDING_APPROVAL' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Pending sx={{ color: theme.palette.warning.main, fontSize: 20 }} />
                          <Chip
                            label={t('classRoom.pendingApproval') || 'Pending Approval'}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.1),
                              color: theme.palette.warning.main,
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {meeting.status === 'PENDING_APPROVAL' ? (
                          <>
                            <Button
                              onClick={() => {
                                dispatch(approveSession(meeting._id));
                                setTimeout(() => {
                                  handleSessionsFetch();
                                }, 500);
                              }}
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckCircle />}
                              sx={{
                                flex: 1,
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              }}
                            >
                              {t('classRoom.approve') || 'Approve'}
                            </Button>
                            <Button
                              onClick={() => {
                                dispatch(rejectSession(meeting._id));
                                setTimeout(() => {
                                  handleSessionsFetch();
                                }, 500);
                              }}
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              sx={{
                                flex: 1,
                                borderRadius: 2,
                                fontWeight: 600,
                                textTransform: 'capitalize',
                              }}
                            >
                              {t('classRoom.reject') || 'Reject'}
                            </Button>
                          </>
                        ) : (
                          <>
                            {meeting.tutoringType === "REMOTE" && meeting.status === 'SCHEDULED' && (
                              <Button
                                disabled={
                                  meeting.startingTime > dayjs() ||
                                  meeting.endingTime < dayjs()
                                }
                                onClick={() => {
                                  navigate(`/session/${meeting._id}/join`);
                                }}
                                variant="contained"
                                size="small"
                                sx={{
                                  flex: 1,
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {t('classRoom.joinSession')}
                              </Button>
                            )}
                            {meeting.status === 'SCHEDULED' && (
                              <Button
                                disabled={meeting.startingTime < dayjs()}
                                onClick={() => {
                                  dispatch(cancelSession(meeting._id));
                                }}
                                variant="outlined"
                                color="error"
                                size="small"
                                startIcon={<Cancel />}
                                sx={{
                                  flex: 1,
                                  borderRadius: 2,
                                  fontWeight: 600,
                                  textTransform: 'capitalize',
                                }}
                              >
                                {t('classRoom.cancel')}
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

const combineDateAndTime = (date, time) => {
  const combinedDateTime = dayjs(date)
    .set("hour", time.hour())
    .set("minute", time.minute());
  return combinedDateTime;
};

export default Detail_class_room;
